// <pos-lighthouse-redemption/> — 燈塔店家終身優惠 Key 兌換頁（owner only）
// see architecture.md#L320 (Subscription vs Entitlement)
// see docs/LIGHTHOUSE_KEY_PROGRAM.md v0.2
//
// 流程：
//   1. owner 在 #/settings/lighthouse 頁面輸入 LH-XXXX-XXXX-XXXX-XXXX
//   2. 送 POST /lighthouse-redeem → RPC redeem_lighthouse_code
//   3. 成功 → refreshSession（讓 JWT 拿到新 entitlement claim）→ 顯示「✅ 已啟用終身 ${plan} 方案」
//   4. 失敗 → 顯示對應 message_zh

import { redeemLighthouseCode } from '../lib/api.js';
import { getSupabase } from '../lib/supabase-client.js';

const escapeHtml = (s) => String(s ?? '').replace(/[&<>"']/g, (c) =>
  ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

const PERK_LABEL = Object.freeze({
  lifetime_standard: '終身標準版',
  lifetime_pro: '終身專業版',
});

class PosLighthouseRedemption extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.submitting = false;
    this.formError = null;
    this.formSuccess = null;
    this.lastUnlocked = null; // { program, perk }
    this.submitTimer = null;
  }

  connectedCallback() {
    this.render();
  }

  disconnectedCallback() {
    if (this.submitTimer) {
      clearTimeout(this.submitTimer);
      this.submitTimer = null;
    }
  }

  render() {
    // Codex review nit：避免在元件卸載後仍重繪 detached shadow root
    // （非同步 await 完成或 timer fire 時元件可能已被 router 換掉）
    if (!this.isConnected) return;

    const { submitting, formError, formSuccess, lastUnlocked } = this;
    const errHtml = formError
      ? `<div class="form-msg form-msg-err">✗ ${escapeHtml(formError)}</div>`
      : '';
    const okHtml = formSuccess
      ? `<div class="form-msg form-msg-ok">✓ ${escapeHtml(formSuccess)}</div>`
      : '';

    const unlockedHtml = lastUnlocked ? `
      <div class="unlocked">
        <div class="unlocked-icon">🏮</div>
        <div>
          <div class="unlocked-title">已啟用 ${escapeHtml(PERK_LABEL[lastUnlocked.perk] || lastUnlocked.perk)}</div>
          <div class="unlocked-meta">方案永久有效，不受月訂閱到期影響。請重新整理頁面以套用。</div>
        </div>
      </div>
    ` : '';

    this.shadowRoot.innerHTML = `
      <style>
        :host { display: block; padding: var(--s-5); max-width: 760px; margin: 0 auto; color: var(--fg-base); }
        h2 { font-family: var(--font-display); color: var(--brand-primary); margin-bottom: var(--s-4); }
        .intro {
          background: var(--bg-elevated);
          padding: var(--s-4);
          border-radius: var(--r-md);
          margin-bottom: var(--s-5);
          line-height: 1.7;
          border-left: 4px solid var(--brand-primary);
        }
        .intro strong { color: var(--brand-primary); }
        form {
          background: var(--bg-elevated);
          padding: var(--s-5);
          border-radius: var(--r-md);
        }
        .row { margin-bottom: var(--s-4); }
        label { display: block; margin-bottom: var(--s-2); color: var(--fg-muted); font-size: 13px; }
        input {
          width: 100%;
          padding: var(--s-3);
          background: var(--bg);
          border: 1px solid var(--border);
          border-radius: var(--r-md);
          color: var(--fg);
          font: inherit;
          font-family: var(--font-mono);
          letter-spacing: .05em;
          text-transform: uppercase;
          box-sizing: border-box;
        }
        input:focus { outline: none; border-color: var(--brand-primary); }
        input:disabled { opacity: .5; cursor: not-allowed; }
        .actions { display: flex; gap: var(--s-3); }
        button {
          min-height: 40px;
          padding: var(--s-3) var(--s-5);
          border-radius: var(--r-md);
          cursor: pointer;
          font: inherit;
          font-weight: 600;
        }
        button:disabled { opacity: .5; cursor: not-allowed; }
        .btn-primary { background: var(--brand-primary); color: white; border: 0; }
        .form-msg {
          padding: var(--s-3) var(--s-4);
          border-radius: var(--r-md);
          margin-bottom: var(--s-4);
          font-size: 14px;
          line-height: 1.5;
        }
        .form-msg-err { background: rgba(220,38,38,.1); color: var(--danger); border: 1px solid var(--danger); }
        .form-msg-ok { background: rgba(34,197,94,.1); color: var(--success); border: 1px solid var(--success); }
        .unlocked {
          display: flex;
          gap: var(--s-4);
          align-items: center;
          padding: var(--s-5);
          background: linear-gradient(135deg, rgba(251,191,36,.15), rgba(217,119,6,.1));
          border: 1px solid #fbbf24;
          border-radius: var(--r-md);
          margin-bottom: var(--s-5);
        }
        .unlocked-icon { font-size: 36px; }
        .unlocked-title { font-size: 16px; font-weight: 700; color: #d97706; margin-bottom: 4px; }
        .unlocked-meta { font-size: 13px; color: var(--fg-muted); line-height: 1.5; }
      </style>

      <h2>🏮 燈塔店家終身優惠 Key</h2>

      <div class="intro">
        <strong>燈塔店家計畫</strong>是給最早 5 家陪我們一起把產品做扎實的合作夥伴。<br/><br/>
        持有 <strong>LH-XXXX-XXXX-XXXX-XXXX</strong> 兌換碼的店家可以在此啟用「終身標準版」權益，
        後續 SaaS 月訂閱到期也不會影響你的功能可用性。<br/><br/>
        每個店家僅限兌換一次。如有疑問請聯繫 Pat。
      </div>

      ${unlockedHtml}

      <form id="f" novalidate>
        ${errHtml}${okHtml}
        <div class="row">
          <label>兌換碼</label>
          <input
            type="text"
            name="code"
            placeholder="LH-XXXX-XXXX-XXXX-XXXX"
            autocomplete="off"
            spellcheck="false"
            minlength="8"
            maxlength="32"
            required
            ${submitting ? 'disabled' : ''}
          />
        </div>
        <div class="actions">
          <button type="submit" class="btn-primary" ${submitting ? 'disabled' : ''}>
            ${submitting ? '兌換中…' : '兌換'}
          </button>
        </div>
      </form>
    `;

    this.shadowRoot.querySelector('#f')?.addEventListener('submit', (e) => this.onSubmit(e));
  }

  async onSubmit(e) {
    e.preventDefault();
    if (this.submitting) return;

    const fd = new FormData(e.target);
    const code = (fd.get('code') || '').toString().trim();
    if (code.length < 8) {
      this.formError = '兌換碼長度不足';
      this.formSuccess = null;
      this.render();
      return;
    }

    this.submitting = true;
    this.formError = null;
    this.formSuccess = null;
    this.render();

    // 12s safety timeout（RPC 內含 200ms constant-time floor + advisory lock，正常 <500ms）
    this.submitTimer = setTimeout(() => {
      if (this.submitting) {
        this.submitting = false;
        this.formError = '請求超時（>12 秒沒回應）。請檢查網路。';
        this.render();
      }
    }, 12000);

    try {
      const res = await redeemLighthouseCode(code);
      clearTimeout(this.submitTimer);
      this.submitTimer = null;

      this.lastUnlocked = { program: res.program, perk: res.perk };
      this.formSuccess = `兌換成功 — 已啟用 ${PERK_LABEL[res.perk] || res.perk}`;

      // 觸發 refreshSession 讓 JWT 拿到新 entitlement claim
      // （不阻塞 UI 反饋；失敗也不影響資料庫已寫入的 entitlement）
      if (res.refresh_session) {
        try {
          await getSupabase().auth.refreshSession();
        } catch (refreshErr) {
          console.warn('[lighthouse] refreshSession failed; user may need to re-login', refreshErr);
        }
      }
    } catch (err) {
      clearTimeout(this.submitTimer);
      this.submitTimer = null;
      this.formError = err?.message_zh || err?.message || '兌換失敗';
    } finally {
      this.submitting = false;
      this.render();
    }
  }
}

customElements.define('pos-lighthouse-redemption', PosLighthouseRedemption);
